import { AvatarTypes, FormInput, ModeHalResource } from '@mode/shared/contract-common';
import { Observable } from 'rxjs';

interface SlackSubScriptionsResource extends ModeHalResource {
  _embedded: {
    slack_subscription_memberships: SlackChannelResource[];
  };
}

interface PublicSlackChannel {
  channel: string;
  channel_id: string;
  channel_type: 'public';
}

interface PrivateSlackChannel {
  channel_id: null;
  channel_type: 'private';
}

type SlackChannel = PublicSlackChannel | PrivateSlackChannel;
type SlackChannelResource = SlackChannel & ModeHalResource;

export interface EmailSubscriptionMembershipResource extends ModeHalResource {
  email: string;
}

interface ReportSubscriptionResource extends ModeHalResource {
  csv_attachments_enabled: boolean;
  data_previews_enabled: boolean;
  data_tables_enabled: boolean;
  pdf_attachments_enabled: boolean;
  report_links_enabled: boolean;
  _embedded: {
    slack_subscription_memberships: SlackSubScriptionsResource;
    email_subscription_memberships: EmailSubscriptionMembershipResource[];
  };
}

interface ReportSubscriptionsResource extends ModeHalResource {
  _embedded: {
    report_subscriptions: ReportSubscriptionResource[];
  };
}

export interface ReportScheduleResource extends ModeHalResource {
  report_name: string;
  report_token: string;
  report_type: 'BaseReport' | 'DatasetReport' | 'Report';
  frequency: string;
  day_of_week: string;
  day_of_month: string;
  delivery: string | null;
  last_scheduled_run: string;
  hour: string;
  minute: string;
  name: string;
  time_zone: string;
  token: string;
  paused: boolean;
  subscribed: boolean;
  subscriber_count: number;
  parameters: Object;
  _embedded: {
    report_subscriptions: ReportSubscriptionsResource;
  };
  valid_interval: boolean;
}

export interface ReportSchedulesResource extends ModeHalResource {
  _embedded: {
    report_schedules: ReportScheduleResource[];
  };
}

export interface EmailMemberships {
  avatar?: AvatarTypes.Avatar;
  deleteUrl: string;
  email: string;
  id: string;
  inWorkspace?: boolean;
}

export interface Subscription {
  id: string | null;
  csvAttachmentsEnabled: boolean;
  dataPreviewsEnabled: boolean;
  dataTablesEnabled: boolean;
  pdfAttachmentsEnabled: boolean;
  reportLinksEnabled: boolean;
  createEmailSubscriptionUrl: string | null;
  _formElements: { [property: string]: FormInput } | undefined;
  slackSubscription: {
    channel?: string;
    channelId: string | null;
    channelType?: string;
  };
}

export interface ReportSchedule {
  id: string;
  cron: Object;
  dayOfMonth: string | number;
  dayOfWeek: string | number;
  frequency: string;
  hour: string | number;
  lastScheduledRun: string;
  minute: string | number;
  name: string;
  paused: boolean;
  parameters: Object;
  paramsMetaData?: FormInput;
  reportName: string;
  reportToken: string;
  reportType: string;
  subscribed: boolean;
  subscriberCount: number;
  timeZone: string;
  token: string;
  canDeleteSchedule: boolean;
  canEditSchedule: boolean;
  delivery: string | null;
  hasSubscription: boolean;
  hasSlackSubscription: boolean;
  hasUpdates: boolean;
  emailMemberships?: EmailMemberships;
  subscription: Subscription | null;
  validInterval: boolean;
}

export interface LoadReportSchedulesPayload {
  reportId: string;
}

export interface DeleteReportSchedulePayload {
  reportId: string;
  scheduleId: string;
}

export interface CreateEmailSubscriptionPayload {
  url: string;
  emailPayload: { membership: { subscriber_email: string } };
  scheduleId: string;
  subscriberCount: number;
}

export interface DeleteEmailSubscriptionPayload {
  url: string;
  scheduleId: string;
  subscriberCount: number;
}

export abstract class ReportSchedulesFacade {
  abstract readonly reportSchedules$: Observable<Array<ReportSchedule> | []>;
  abstract readonly loading$: Observable<boolean>;

  public abstract loadReportSchedules(props: LoadReportSchedulesPayload): void;

  public abstract deleteReportSchedule(props: DeleteReportSchedulePayload): void;

  public abstract createEmailSubscription(props: CreateEmailSubscriptionPayload): void;

  public abstract deleteEmailSubscription(props: DeleteEmailSubscriptionPayload): void;
}
